import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Typography, Divider } from "@mui/material";
import PaginationControls from "../common/table/PaginationControls";

import { useLoading } from "../../context/LoadingContext";
import {
  getPharmacyOpportunityColumns,
  getProviderOpportunityColumns,
} from "../common/table/columns";
import {
  fetchPharmacyOpportunities,
  fetchProviderOpportunities,
} from "../../api/provider";
import CommonTable from "../common/table/commonTable";

import "./Searchpage.css";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import DeleteTaskModal from "../modals/DeleteTaskModal";
import EditTaskModal from "../modals/EditTaskModal";
import { deleteManualTask, updateManualTask } from "../../api/manualTask";
import SelectionRowCheckbox from "./SelectionRowCheckbox";
import SelectionHeaderCheckbox from "./SelectionHeaderCheckbox";

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const formatOpportunity = (item, idx) => ({
  id: idx + 1,
  opportunityId: item.opportunityId,
  slo: item.slo,
  category: item.member?.fullName || "",
  drug: item.medication?.drugName
    ? (() => {
        const lower = item.medication.drugName.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })()
    : "Unknown",
  drugId: item.medication?.drugId || "",
  isGraduated: item.isGraduated || false,
  outreachReason: capitalizeFirstLetter(item.manualTask?.outreachReason) || "",
  programId: item.program?.id || "",
  method: item.recentActivity?.outreachMethod || "",
  pharmacy: item.pharmacy?.name || "N/A",
  pharmacyPhone: item.pharmacy?.contact || "N/A",
  recentActivity: {
    status: item.recentActivity?.outcome
      ? capFirst(item.recentActivity.outcome)
      : "",
    at: item.recentActivity?.timestamp || "",
    role: item.recentActivity?.recentActivityRole || "",
  },
  status: item?.recentActivity?.status
    ? capitalizeFirstLetter(item.recentActivity?.status)
    : "",
  manualTaskId: item.manualTask?.id,
});

const buildDispositionPayload = (rawApiData, selectedRows) =>
  selectedRows.map((row) => {
    const idx = row.id - 1;
    return rawApiData?.[idx]
      ? formatOpportunity(rawApiData[idx], idx)
      : { ...row };
  });

const PharmacyMember = (props) => {
  const { setDispositionOpportunity } = props;
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [results, setResults] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  const [rawApiData, setRawApiData] = useState([]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedRows.length === 0) return;
    setIsDeleteTaskModalOpen(true);
  }, [selectedRows]);

  const { onDeleteSelectedRef, onSelectedCountChange } = props;
  useEffect(() => {
    if (onDeleteSelectedRef) {
      onDeleteSelectedRef.current = handleDeleteSelected;
    }
    if (onSelectedCountChange) {
      onSelectedCountChange(selectedRows.length);
    }
  }, [
    onDeleteSelectedRef,
    onSelectedCountChange,
    handleDeleteSelected,
    selectedRows,
  ]);

  const handleConfirmDelete = async () => {
    try {
      showLoading();

      const idsString = selectedRows
        .map((row) => row.manualTask?.id)
        .filter((id) => id !== undefined && id !== null && id !== "")
        .join(",");
      await deleteManualTask(idsString);

      let data = {};
      if (props.pharmacyId) {
        data = await fetchPharmacyOpportunities(pharmacyId, "");
      } else if (props.providerId) {
        data = await fetchProviderOpportunities(providerId, "");
      }

      const mappedResults = (data.content || []).map((item, idx) => ({
        member: {
          memberId: item.member?.id?.toString() || "",
          name: item.member?.fullName
            ? item.member.fullName.charAt(0).toUpperCase() +
              item.member.fullName.slice(1).toLowerCase()
            : "",
          language: item.member?.language || "",
          timeZone: item.member?.timezone || "",
          gender: "",
          age: item.member?.dob
            ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
            : "",
          dob: item.member?.dob || "",
          knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
          contact: {
            phone: item.member?.phoneNumber || "",
            email: item.member?.email || "",
          },
          address: "",
        },
        memberBadge: item.member?.memberBadge || item.memberBadge || "",
        type: item.outreachReason || "",
        provider: {
          name: item?.provider?.fullName || "",
          contact: item?.provider?.phone || "",
        },
        pharmacy: {
          name: item.pharmacy?.name || "",
          contact: item.pharmacy?.phone || "",
        },
        programs:
          item.memberPrograms?.map((p) => ({
            name: p.name || "",
            status: p.status || "",
          })) || [],
        recentActivity: {
          status: item.recentActivity?.outcome ?? "",
          role: item.recentActivity?.role ?? item.recentActivityRole ?? "",
          timestamp:
            item.recentActivity?.timestamp ?? item.recentActivityAt ?? "",
        },
        status: item.recentActivity?.status || "",
        dueDate: item.dueDate || "",
        id: idx + 1,
        outreachReason: item.outreachReason || "",
        manualTask: {
          id: item.manualTask?.id || "",
          noOfAttempts: item.manualTask?.attemptCount || 0,
          taskNote: item.manualTask?.note || "",
          outreachReason: item.manualTask?.outreachReason || "",
          dueDate: item.manualTask?.dueDate || "",
        },
      }));
      setResults(mappedResults);
      setTotalRows(data.totalElements || mappedResults.length);
      setSelectedRows([]);
      setIsDeleteTaskModalOpen(false);
      hideLoading();
    } catch (error) {
      console.error("Unexpected error", error);
      hideLoading();
    } finally {
      hideLoading();
    }
  };
  const tableRef = useRef(null);

  const pharmacyId =
    props.pharmacyId || props.selectedMemberId || props.memberId || props.id;
  const providerId =
    props.providerId || props.selectedMemberId || props.memberId || props.id;

  const mapOpportunityResults = (data) => {
    return (data.content || []).map((item, idx) => ({
      member: {
        memberId: item.member?.id?.toString() || "",
        name: item.member?.fullName
          ? item.member.fullName.charAt(0).toUpperCase() +
            item.member.fullName.slice(1).toLowerCase()
          : "",
        language: item.member?.language || "",
        timeZone: item.member?.timezone || "",
        gender: "",
        age: item.member?.dob
          ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
          : "",
        dob: item.member?.dob || "",
        knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
        contact: {
          phone: item.member?.phoneNumber || "",
          email: item.member?.email || "",
        },
        address: "",
      },
      memberBadge: item.member?.memberBadge || item.memberBadge || "",
      type: item.outreachReason || "",
      provider: {
        name: item?.provider?.fullName || "",
        contact: item?.provider?.phone || "",
      },
      pharmacy: {
        name: item.pharmacy?.name || "",
        contact: item.pharmacy?.phone || "",
      },
      programs:
        item.memberPrograms?.map((p) => ({
          name: p.name || "",
          status: p.status || "",
        })) || [],
      recentActivity: {
        status: item.recentActivity?.outcome ?? "",
        role: item.recentActivity?.role ?? item.recentActivityRole ?? "",
        timestamp:
          item.recentActivity?.timestamp ?? item.recentActivityAt ?? "",
      },
      status: item.recentActivity?.status || "",
      dueDate: item.dueDate || "",
      id: idx + 1,
      outreachReason: item.outreachReason || "",
      manualTask: {
        id: item.manualTask?.id || "",
        noOfAttempts: item.manualTask?.attemptCount || 0,
        taskNote: item.manualTask?.note || "",
        outreachReason: item.manualTask?.outreachReason || "",
        dueDate: item.manualTask?.dueDate || "",
      },
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data = {};
        if (props.pharmacyId) {
          data = await fetchPharmacyOpportunities(pharmacyId, "");
        } else if (props.providerId) {
          data = await fetchProviderOpportunities(providerId, "");
        } else {
          setResults([]);
          setTotalRows(0);
          return;
        }
        const mappedResults = mapOpportunityResults(data);
        setResults(mappedResults);
        setTotalRows(data.totalElements || mappedResults.length);
      } catch (error) {
        console.error("Unexpected error", error);
        setResults([]);
        setTotalRows(0);
      } finally {
        console.error("In finally block");
      }
    };
    if (props.pharmacyId || props.providerId) void fetchData();
  }, [pharmacyId, providerId, props.pharmacyId, props.providerId]);

  const handleEditOpportunity = (row) => {
    const base = row.manualTask || {};
    let recipientType;
    if (props.pharmacyId) {
      recipientType = "pharmacy";
    } else if (props.providerId) {
      recipientType = "provider";
    } else {
      recipientType = "member";
    }

    const task = {
      recipientType,

      recipientName:
        (recipientType === "pharmacy" &&
          (row.pharmacy?.name || row.member?.name)) ||
        (recipientType === "provider" &&
          (row.provider?.name || row.member?.name)) ||
        row.member?.name ||
        "",
      recipientId: (() => {
        if (recipientType === "pharmacy") {
          return row.pharmacy?.id || "";
        } else if (recipientType === "provider") {
          return row.provider?.id || "";
        } else {
          return row.member?.memberId || row.member?.id || "";
        }
      })(),
      timeZone: row.member?.timeZone || row.member?.timezone || "",
      language: row.member?.language || "",
      dueDate: base.dueDate || row.dueDate || new Date(),
      outreachReason: base.outreachReason || row.outreachReason || "",
      details: base.taskNote || base.note || "",

      linkedMember: row.member?.memberId || row.member?.id || "",
      program: row.programs?.[0]?.id || "",
      recentActivityStatus: row.recentActivity?.status || "",
      recentActivityAt: row.recentActivity?.timestamp || "",
      linkedMemberName: row.member?.name || "",

      manualTaskId: base.id || row.manualTask?.id || "",
    };
    setSelectedTask(task);
    setIsEditTaskModalOpen(true);
  };

  const columns = pharmacyId
    ? getProviderOpportunityColumns(
        props.onQuickView,
        handleEditOpportunity,
        true,
      )
    : getPharmacyOpportunityColumns(
        props.onQuickView,
        handleEditOpportunity,
        true,
      );

  const handleRowSelect = useCallback(
    (row, checked) => {
      const updated = checked
        ? [...selectedRows, row]
        : selectedRows.filter((r) => r.id !== row.id);
      setSelectedRows(updated);
      if (!setDispositionOpportunity) return;
      if (updated.length === 0) {
        setDispositionOpportunity(null);
        return;
      }
      const formatted = buildDispositionPayload(rawApiData, updated);
      setDispositionOpportunity({ data: formatted, count: formatted.length });
    },
    [selectedRows, setDispositionOpportunity, rawApiData],
  );

  const handleHeaderSelect = useCallback(
    (pageRows, checked) => {
      let newSelected;
      if (checked) {
        newSelected = [
          ...selectedRows,
          ...pageRows.filter((r) => !selectedRows.some((sr) => sr.id === r.id)),
        ];
      } else {
        newSelected = selectedRows.filter(
          (r) => !pageRows.some((pr) => pr.id === r.id),
        );
      }
      setSelectedRows(newSelected);
      if (!setDispositionOpportunity) return;
      if (newSelected.length === 0) {
        setDispositionOpportunity(null);
        return;
      }
      const formatted = buildDispositionPayload(rawApiData, newSelected);
      setDispositionOpportunity({ data: formatted, count: formatted.length });
    },
    [selectedRows, setDispositionOpportunity, rawApiData],
  );

  const selectionCell = useCallback(
    ({ row }) => (
      <SelectionRowCheckbox
        rowId={row.original.id}
        selectedRows={selectedRows}
        onChange={(checked) => handleRowSelect(row.original, checked)}
      />
    ),
    [selectedRows, handleRowSelect],
  );

  const selectionHeader = useCallback(
    () => (
      <SelectionHeaderCheckbox
        results={results}
        pageIndex={pageIndex}
        pageSize={pageSize}
        selectedRows={selectedRows}
        onToggle={handleHeaderSelect}
      />
    ),
    [results, pageIndex, pageSize, selectedRows, handleHeaderSelect],
  );

  const patchedColumns = useMemo(
    () =>
      columns.map((col) =>
        col.id === "selection"
          ? { ...col, cell: selectionCell, header: selectionHeader }
          : col,
      ),
    [columns, selectionCell, selectionHeader],
  );

  const mapOpportunityResults2 = (data) => {
    return (data.content || []).map((item, idx) => ({
      member: {
        memberId: item.member?.id?.toString() || "",
        name: item.member?.fullName
          ? item.member.fullName.charAt(0).toUpperCase() +
            item.member.fullName.slice(1).toLowerCase()
          : "",
        language: item.member?.language || "",
        timeZone: item.member?.timezone || "",
        gender: "",
        age: item.member?.dob
          ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
          : "",
        dob: item.member?.dob || "",
        knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
        contact: {
          phone: item.member?.phoneNumber || "",
          email: item.member?.email || "",
        },
        address: "",
      },
      memberBadge: item.member?.memberBadge || item.memberBadge || "",
      type: item.outreachReason || "",
      provider: {
        name: item?.provider?.fullName || "",
        contact: item?.provider?.phone || "",
      },
      pharmacy: {
        name: item.pharmacy?.name || "",
        contact: item.pharmacy?.phone || "",
      },
      programs:
        item.memberPrograms?.map((p) => ({
          name: p.name || "",
          status: p.status || "",
        })) || [],
      recentActivity: {
        status: item.recentActivity?.outcome ?? item.status ?? "",
        role: item.recentActivity?.role ?? item.recentActivityRole ?? "",
        timestamp:
          item.recentActivity?.timestamp ?? item.recentActivityAt ?? "",
      },
      dueDate: item.dueDate || "",
      id: idx + 1,
      outreachReason: item.outreachReason || "",
      manualTask: {
        id: item.manualTask?.id || "",
        noOfAttempts: item.manualTask?.attemptCount || 0,
        taskNote: item.manualTask?.note || "",
        outreachReason: item.manualTask?.outreachReason || "",
        dueDate: item.manualTask?.dueDate || "",
      },
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data = {};
        if (props.pharmacyId) {
          data = await fetchPharmacyOpportunities(pharmacyId, "");
        } else if (props.providerId) {
          data = await fetchProviderOpportunities(providerId, "");
        } else {
          setResults([]);
          setTotalRows(0);
          setRawApiData([]);
          return;
        }
        setRawApiData(data.content || []);
        const mappedResults = mapOpportunityResults2(data);
        setResults(mappedResults);
        setTotalRows(data.totalElements || mappedResults.length);
      } catch (error) {
        console.error("Unexpected error", error);
        setResults([]);
        setTotalRows(0);
        setRawApiData([]);
      } finally {
        console.error("In finally block");
      }
    };
    if (props.pharmacyId || props.providerId) void fetchData();
  }, [
    pharmacyId,
    providerId,
    props.pharmacyId,
    props.providerId,
    props.opportunitiesReloadKey,
  ]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          pt: 3,
          px: 3,
          borderRadius: 2,
          border: "1px solid #E8E8E9",
          boxShadow: "none",
          width: "100%",
          height: "73vh",
          boxSizing: "border-box",
          mt: 2,
          mb: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: 700, mb: 0.5, fontStyle: "Bold", fontSize: "20px" }}
        >
          Opportunities
        </Typography>

        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignrows: "center",
            mb: 1.5,
          }}
        ></Box>
        <DeleteTaskModal
          open={isDeleteTaskModalOpen}
          onClose={() => setIsDeleteTaskModalOpen(false)}
          onConfirm={handleConfirmDelete}
          isMultiple={selectedRows.length > 1}
        />
        <EditTaskModal
          open={isEditTaskModalOpen}
          onClose={() => {
            setIsEditTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onSave={async (updated) => {
            if (!selectedTask) return;

            showLoading();
            try {
              showLoading();

              const manualTaskId = selectedTask.manualTaskId;
              if (manualTaskId) {
                const payload = {
                  dueDate:
                    updated.dueDate instanceof Date
                      ? updated.dueDate.toISOString().split("T")[0]
                      : updated.dueDate,
                  outreachReason: updated.outreachReason,
                  note: updated.note || updated.details || "",
                };
                await updateManualTask(manualTaskId, payload);
              }
              const newDue =
                updated.dueDate instanceof Date
                  ? updated.dueDate.toISOString().split("T")[0]
                  : updated.dueDate;
              setResults((prev) =>
                prev.map((r) => {
                  if (manualTaskId && r.manualTask?.id === manualTaskId) {
                    return {
                      ...r,
                      manualTask: {
                        ...r.manualTask,
                        dueDate: newDue,
                        outreachReason: updated.outreachReason,
                        taskNote: updated.note || updated.details,
                      },
                      dueDate: newDue,
                      outreachReason: updated.outreachReason,
                    };
                  }
                  return r;
                }),
              );
            } catch (error_) {
              console.error("Unexpected error", error_);
            } finally {
              hideLoading();
              setIsEditTaskModalOpen(false);
              setSelectedTask(null);
            }
          }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CommonTable
            ref={tableRef}
            columns={patchedColumns}
            data={results.slice(
              pageIndex * pageSize,
              (pageIndex + 1) * pageSize,
            )}
            tableHeight="calc(73vh - 190px)"
          />
          <Box
            sx={{
              pt: 1,
              pb: 1,
              "& .MuiTablePagination-root": { borderTop: "none" },
              background:
                "linear-gradient(to top, #FFFFFF 70%, rgba(255,255,255,0.6))",
            }}
          >
            <PaginationControls
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRows={totalRows}
              pageCount={Math.ceil(totalRows / pageSize)}
              canPreviousPage={pageIndex > 0}
              canNextPage={pageIndex < Math.ceil(totalRows / pageSize) - 1}
              onPageSizeChange={(size) => {
                setPageSize(Number(size));
                setPageIndex(0);
              }}
              onPageChange={(page) => {
                setPageIndex(page);
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

PharmacyMember.propTypes = {
  setDispositionOpportunity: PropTypes.func,
  onDeleteSelectedRef: PropTypes.shape({ current: PropTypes.any }),
  onSelectedCountChange: PropTypes.func,
  pharmacyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  providerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onQuickView: PropTypes.func,
  opportunitiesReloadKey: PropTypes.number,
  selectedMemberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PharmacyMember;
